import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { adminService } from '../../services/admin';
import { isTrialExpired, type BillingStatus, type Tenant } from '@traaker/shared';

const PAGE_SIZE = 50;

const BILLING_COLORS: Record<BillingStatus, string> = {
  pending: '#8B9DC3',
  trialing: '#00DDB3',
  active: '#00DDB3',
  past_due: '#F59E0B',
  suspended: '#EF4444',
  cancelled: '#6B7280',
};

/**
 * Exact stored monthly/contract amount for a tenant — never the ambiguous Stripe
 * catalog list price.
 * - enterprise / manual → negotiated `contractAmountCents`
 * - agency → `monthlyAllocation × pricePerCreditCents` (both negotiated + stored)
 * - self-serve (basic/pro/elite) → '—' (no exact amount stored on the tenant)
 */
function contractLine(t: Tenant): string {
  if (t.contractAmountCents != null) {
    return `${(t.contractAmountCents / 100).toFixed(0)} € HT/${t.contractInterval || 'month'}`;
  }
  const agency = t.settings?.agencyCredits;
  if (t.tier === 'agency' && agency?.pricePerCreditCents != null && agency.monthlyAllocation != null) {
    const euros = (agency.monthlyAllocation * agency.pricePerCreditCents) / 100;
    return `${euros.toFixed(0)} € HT/month`;
  }
  return '—';
}

export default function AdminTenants() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tenant-ID lookup. A matched id replaces the list with the single hit; the
  // point lookup (GET /admin/tenants/:id) needs no search index.
  const [searchId, setSearchId] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchHit, setSearchHit] = useState<Tenant | null>(null);

  // Load the first page.
  useEffect(() => {
    adminService
      .listTenantsPage({ limit: PAGE_SIZE })
      .then((res) => {
        setTenants(res.items);
        setNextCursor(res.nextCursor);
      })
      .catch((err) => setError(err.message || t('admin.list.loadError')))
      .finally(() => setLoading(false));
  }, [t]);

  const loadMore = () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    adminService
      .listTenantsPage({ limit: PAGE_SIZE, cursor: nextCursor })
      .then((res) => {
        setTenants((prev) => [...prev, ...res.items]);
        setNextCursor(res.nextCursor);
      })
      .catch((err) => setError(err.message || t('admin.list.loadError')))
      .finally(() => setLoadingMore(false));
  };

  const runSearch = async () => {
    const id = searchId.trim();
    if (!id) return;
    setSearching(true);
    setSearchError(null);
    setSearchHit(null);
    try {
      const { tenant } = await adminService.getTenant(id);
      setSearchHit(tenant);
    } catch {
      setSearchError(t('admin.list.searchNoResult', { id }));
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchId('');
    setSearchHit(null);
    setSearchError(null);
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  // When a search hit exists, show only it; otherwise the paginated list.
  const rows = searchHit ? [searchHit] : tenants;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('admin.list.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {searchHit
              ? t('admin.list.searchResult')
              : `${t('admin.list.loadedCount', { count: tenants.length })}${nextCursor ? t('admin.list.moreAvailable') : ''}`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/tenants/new')}>
          {t('admin.list.newTenant')}
        </Button>
      </Box>

      {/* Search by tenant ID */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('admin.list.searchPlaceholder')}
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          error={!!searchError}
          helperText={searchError || ' '}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (searchId || searchHit) && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch} aria-label={t('admin.list.clearSearch')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          onClick={runSearch}
          disabled={searching || !searchId.trim()}
          sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
        >
          {searching ? <CircularProgress size={18} /> : t('admin.list.search')}
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.list.colName')}</TableCell>
                <TableCell>{t('admin.list.colEmail')}</TableCell>
                <TableCell>{t('admin.list.colDomain')}</TableCell>
                <TableCell>{t('admin.list.colTier')}</TableCell>
                <TableCell>{t('admin.list.colBilling')}</TableCell>
                <TableCell>{t('admin.list.colMode')}</TableCell>
                <TableCell>{t('admin.list.colContract')}</TableCell>
                <TableCell>{t('admin.list.colCreated')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((tn) => (
                <TableRow
                  key={tn.tenantId}
                  hover
                  onClick={() => navigate(`/admin/tenants/${tn.tenantId}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{tn.name}</TableCell>
                  <TableCell>{tn.ownerEmail || '—'}</TableCell>
                  <TableCell>{tn.domain}</TableCell>
                  <TableCell>
                    <Chip label={tn.tier} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // An expired block_on_expiry trial stays billingStatus='trialing';
                      // surface it as "essai terminé" so the list matches reality.
                      const expired = isTrialExpired(tn, Date.now());
                      // A pending cancellation stays billingStatus='active' until the
                      // period ends — surface it so an operator can spot accounts on
                      // their way out (any cancel form: cancelAtPeriodEnd or cancel_at,
                      // both captured on the tenant as cancelAtPeriodEnd).
                      const cancelScheduled = !expired && tn.cancelAtPeriodEnd === true;
                      const color = expired
                        ? BILLING_COLORS.suspended
                        : cancelScheduled
                          ? BILLING_COLORS.past_due
                          : BILLING_COLORS[tn.billingStatus];
                      const label = expired
                        ? t('admin.list.trialEnded')
                        : cancelScheduled
                          ? t('admin.list.cancelScheduled')
                          : tn.billingStatus;
                      return (
                        <Chip
                          label={label}
                          size="small"
                          sx={{ bgcolor: `${color}20`, color, fontWeight: 600 }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>{tn.billingMode}</TableCell>
                  <TableCell>{contractLine(tn)}</TableCell>
                  <TableCell>{new Date(tn.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('admin.list.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination — hidden while a single search hit is shown */}
      {!searchHit && nextCursor && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <CircularProgress size={20} /> : t('admin.list.loadMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
